import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ui/Avatar';
import { useAuth } from '@/auth/AuthProvider';
import { useLogout } from '@/auth/hooks';
import { useSubscription } from '@/billing/hooks';
import { useT } from '@/i18n/useT';
import { ROUTES } from '@/lib/config';
import { LanguageSwitcher } from './LanguageSwitcher';

/**
 * Account menu for the signed-in top bar: an avatar button that opens a dropdown
 * showing who's signed in, a link to the account page, and a clear Sign out.
 * Replaces the easy-to-miss text link so the logged-in state + logout are
 * obvious. Closes on outside-click or Escape.
 */
export function ProfileMenu() {
  const { t } = useT();
  const { user } = useAuth();
  const logout = useLogout();
  const sub = useSubscription();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  async function signOut() {
    setOpen(false);
    await logout.mutateAsync();
    navigate(ROUTES.login, { replace: true });
  }

  return (
    <div className="profile-menu" ref={ref}>
      <button
        type="button"
        className="profile-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('nav.accountMenu')}
      >
        <Avatar name={user.name} avatarUrl={user.avatarUrl} size={38} />
      </button>
      {open && (
        <div className="profile-dropdown" role="menu">
          <div className="profile-dropdown-head">
            <Avatar name={user.name} avatarUrl={user.avatarUrl} size={44} />
            <div className="profile-dropdown-id">
              <div className="profile-dropdown-name">{user.name}</div>
              <div className="profile-dropdown-email">{user.email}</div>
              {!sub.isLoading && sub.data && sub.data.billingConfigured && (
                <span
                  className={`plan-badge plan-${
                    sub.data.isOperator
                      ? 'op'
                      : sub.data.status === 'trialing'
                        ? 'trial'
                        : sub.data.status === 'past_due'
                          ? 'due'
                          : sub.data.active
                            ? 'pro'
                            : 'free'
                  }`}
                >
                  {sub.data.isOperator
                    ? t('billing.badgeOperator')
                    : sub.data.status === 'trialing'
                      ? t('billing.badgeTrial')
                      : sub.data.status === 'past_due'
                        ? t('billing.badgeDue')
                        : sub.data.active
                          ? t('billing.badgePro')
                          : t('billing.badgeFree')}
                </span>
              )}
            </div>
          </div>
          <div className="profile-dropdown-sep" />
          <LanguageSwitcher />
          <div className="profile-dropdown-sep" />
          <Link
            to={ROUTES.account}
            className="profile-dropdown-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            {t('nav.account')}
          </Link>
          <Link
            to={ROUTES.bot}
            className="profile-dropdown-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            {t('nav.bot')}
          </Link>
          <Link
            to={ROUTES.billing}
            className="profile-dropdown-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            {t('nav.billing')}
          </Link>
          <button
            type="button"
            className="profile-dropdown-item danger"
            role="menuitem"
            onClick={signOut}
            disabled={logout.isPending}
          >
            {logout.isPending ? '…' : t('nav.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
